import { useParams } from "react-router-dom";
import { Page, Text, View, Document } from "@react-pdf/renderer";
import { useFindById, useListRecords } from "../../server/hooks/getData";
import { Viewer } from "../lib/PdfViewer";
import { styles } from "./styles";
import { formatDate } from "../../lib/dateHelper";
import { useEffect, useRef, useState } from "react";
import { listRecords } from "../../server/fetchFunctions";
import { certstyle } from "./certStyles";

// Create Document Component
export default function CertificatePdf_ENG() {
  const id = useParams().id || "";
  const paramSetRef = useRef<Set<any>>(new Set());
  const passed = useRef(false);

  const { data: job, loading: jobL } = useFindById("jobs", [], id);

  const { data: initCertificate, loading: initCertL } = useListRecords("certificate", [id], {
    job_id: id,
  });
  const { data: initCertParams, loading: initCertParamsL } = useListRecords(
    "certificateResults",
    [initCertificate.data[0]],
    {
      certificate_id: initCertificate.data[0]?.id,
    }
  );
  const { data: siPrefix, loading: siPrefixL } = useListRecords("siprefix", [], null);

  const [cert, setCert] = useState<{ [key: string]: any }>({});
  const [results, setResults] = useState<any[]>([]);
  const [etalons, setEtalons] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const listEtalons = async () => {
      const etalonSet = new Set(initCertParams.data.map((item: any) => item.etalon_id));
      const etalons = await listRecords("etalon", { id: [...etalonSet] });
      setEtalons(etalons.data);
    };
    if (initCertificate.data[0] && initCertParams.data.length !== 0 && !siPrefixL && !jobL) {
      setCert(initCertificate.data[0]);
      setResults(initCertParams.data);
      passed.current = initCertParams.data.filter((i) => i.visible === 1 && i.passed === 0).length === 0 ? true : false;
      listEtalons();
      paramSetRef.current = new Set(
        initCertParams.data
          .sort((a: any, b: any) => a.device_function_sort_order - b.device_function_sort_order)
          .filter((item: any) => item.visible === 1)
          .map((item: any) => item.device_function)
      );
      setLoading(false);
    }
  }, [initCertL, initCertParamsL, siPrefixL, jobL]);
  const pageOne = (
    <Page size="A4" style={styles.page}>
      <View>
        <View style={[styles.row, certstyle.header, { marginBottom: 5 }]}>
          <View>
            <Text style={[styles.fs_1, styles.fw_700]}>COMMED KFT</Text>
          </View>
          <View style={{ width: 150 }}>
            <View style={[styles.row]}>
              <Text style={[styles.fs_3]}>Certificate No:</Text>
              <Text style={[styles.fs_3, styles.fw_700]}>{`${cert.cal_number_prefix ?? null}${cert.cal_number}`}</Text>
            </View>
            <View style={[styles.row]}>
              <Text style={[styles.fs_3]}>Issue date:</Text>
              <Text style={[styles.fs_3]}>{formatDate(cert.cal_date)}</Text>
            </View>
          </View>
        </View>
        <Text>Calibration laboratory</Text>
      </View>
      <View style={{ fontSize: 18, textAlign: "center", marginTop: 20, marginBottom: -5, fontWeight: 600 }}>
        <Text>CALIBRATION CERTIFICATE</Text>
      </View>

      <View style={[styles.col, certstyle.section]}>
        <View style={[styles.row, { justifyContent: "flex-start" }]}>
          <Text style={[styles.fw_700, { width: 100 }]}>Object of calibration:</Text>
          <Text>{cert.dev_description}</Text>
        </View>

        <View style={[styles.row]}>
          <Text style={[styles.fw_700, { width: 100 }]}>Specification of the object:</Text>
          <View style={[styles.row, { justifyContent: "flex-start", flexGrow: 1 }]}>
            <View style={{ flexGrow: 1 }}>
              <Text>Manufacturer:</Text>
              <Text>{cert.dev_manufacturer}</Text>
            </View>
            <View style={{ flexGrow: 1 }}>
              <Text>Type:</Text>
              <Text>{cert.dev_type}</Text>
            </View>
            <View style={{ flexGrow: 1 }}>
              <Text>Serial no:</Text>
              <Text>{cert.serial_no}</Text>
            </View>
          </View>
        </View>
      </View>

      <View style={[styles.col, certstyle.section]}>
        <View style={[styles.row, { justifyContent: "flex-start" }]}>
          <Text style={[styles.fw_700, { width: 100 }]}>Customer:</Text>
          <View>
            <Text>{cert.client_name}</Text>
            <Text>{cert.client_address}</Text>
          </View>
        </View>
      </View>

      <View style={[styles.col, certstyle.section]}>
        <View style={[styles.row, { justifyContent: "flex-start" }]}>
          <Text style={[styles.fw_700, { width: 100 }]}>Place of calibration:</Text>
          <View>
            <Text>2890 Tata, Kocsi utca 17-19.</Text>
          </View>
        </View>
        <View style={[styles.row, { justifyContent: "flex-start" }]}>
          <Text style={[styles.fw_700, { width: 100 }]}>Calibration technician:</Text>
          <View>
            <Text>{cert.employee}</Text>
          </View>
        </View>
      </View>

      <View style={[styles.col, certstyle.section]}>
        <View style={[styles.row, { justifyContent: "flex-start" }]}>
          <Text style={[styles.fw_700]}>Used standards:</Text>
        </View>
        <View style={[styles.row, { justifyContent: "flex-start" }]}>
          <Text style={{ width: "25%" }}>Instrument:</Text>
          <View style={{ width: "25%", marginLeft: 5 }}>
            <Text>Manufacturer:</Text>
          </View>
          <View style={{ width: "25%", marginLeft: 5 }}>
            <Text>Type:</Text>
          </View>
          <View style={{ width: "25%", marginLeft: 5 }}>
            <Text>Serial no:</Text>
          </View>
        </View>
        {etalons.map((item: any, index) => (
          <View key={index} style={[styles.row, { justifyContent: "flex-start" }]}>
            <Text style={{ width: "25%" }}>{item.description}</Text>
            <View style={{ width: "25%", marginLeft: 5 }}>
              <Text>{item.manufacturer}</Text>
            </View>
            <View style={{ width: "25%", marginLeft: 5 }}>
              <Text>{item.type}</Text>
            </View>
            <View style={{ width: "25%", marginLeft: 5 }}>
              <Text>{item.serial_no}</Text>
            </View>
          </View>
        ))}
        <View style={[styles.row, { justifyContent: "flex-start" }]}>
          <Text style={{ width: "25%" }}>Hő- és páratartalommérő</Text>
          <View style={{ width: "25%", marginLeft: 5 }}>
            <Text>HT Italia</Text>
          </View>
          <View style={{ width: "25%", marginLeft: 5 }}>
            <Text>HT 167</Text>
          </View>
          <View style={{ width: "25%", marginLeft: 5 }}>
            <Text>102671</Text>
          </View>
        </View>
      </View>

      <View style={[styles.col, certstyle.section]}>
        <View style={[styles.row, { justifyContent: "flex-start" }]}>
          <Text style={[styles.fw_700, { width: 100 }]}>Calibration procedure:</Text>
          <View>
            <Text>Comparison according to procedure CT-KE-04:2019</Text>
          </View>
        </View>
      </View>

      <View style={[styles.row, certstyle.section]}>
        <Text style={[styles.fw_700, { width: 200 }]}>Enviromental conditions:</Text>
        <View style={[styles.row, { justifyContent: "flex-start", flexGrow: 1 }]}>
          <View style={{ flexGrow: 1 }}>
            <Text>Temperature:</Text>
            <Text>23 ± 3 C°</Text>
          </View>
          <View style={{ flexGrow: 1 }}>
            <Text>Humidity:</Text>
            <Text>50 ± 20% RH</Text>
          </View>
          <View style={{ flexGrow: 1 }}>
            <Text>Main voltage:</Text>
            <Text>230 V ± 10%</Text>
          </View>
        </View>
      </View>

      <View style={[styles.col, certstyle.section]}>
        <View style={[styles.col, { justifyContent: "flex-start" }]}>
          <Text style={[styles.fw_700]}>Traceability:</Text>
          <Text>
            Based on the calibration certificates of the standards, measurements can be traced back to national
            standards.
          </Text>
        </View>
        <View style={[styles.col, { justifyContent: "flex-start", marginTop: 10 }]}>
          <Text style={[styles.fw_700]}>Measurement uncertainty:</Text>
          <Text>
            The reported expanded measurement uncertainty is the standard uncertainty multiplied by k = 2, which
            corresponds to approximately 95% coverage probability in the case of a normal distribution. The standard
            uncertaintym was determined in accordance with publication EA-4/02.
          </Text>
        </View>
      </View>

      <View style={[styles.col, certstyle.section]}>
        <View style={[styles.row, { justifyContent: "flex-start" }]}>
          <Text style={[styles.fw_700, { width: 100 }]}>Qualification:</Text>
          <View>
            <Text>
              {job.qualify === 0 ? "Customer did not request" : passed.current === true ? "Passed" : "Not passed"}
            </Text>
          </View>
        </View>
        <View style={[styles.row, { justifyContent: "flex-start" }]}>
          <Text style={[styles.fw_700, { width: 100 }]}>Qualification based on:</Text>
          <View>
            <Text>{job.qualify === 0 ? "---" : cert.based_on}</Text>
          </View>
        </View>
      </View>

      <View style={[styles.col]}>
        <View style={[styles.row, { justifyContent: "flex-start", fontStyle: "italic" }]}>
          <Text style={{ width: 100 }}>Notes:</Text>
          <View>
            <Text>The measurement results refer to the metrological characteristics found during calibration.</Text>
            <Text>{cert.notes}</Text>
          </View>
        </View>
      </View>

      <View style={certstyle.footer} fixed>
        <View style={[styles.row]}>
          <Text style={{ fontSize: 10, marginTop: 5 }}>A calibration certificate can be issued. </Text>
          <Text style={{ fontSize: 10, marginTop: 5 }}>
            ............................................................................
          </Text>
        </View>
        <View style={[styles.row]}>
          <Text style={{ fontSize: 10, marginTop: 5 }}></Text>
          <Text style={{ fontSize: 10, marginTop: 5 }}>{`${cert.signer} laboratory manager`}</Text>
        </View>
        <Text style={[styles.fs_4, { marginBottom: -5, marginTop: 5, fontSize: 8 }]}>
          The certificate is only valid and may be copied in its full form and scope; an abstract copy may only be made
          with the consent of the issuing organization.
        </Text>
        <View style={[styles.row, certstyle.section]}>
          <View>
            <Text>Commed Kft | Address: 2890 Tata, Kocsi utca 17-19. | VAT: 10886854-2-11</Text>
            <Text>Tel: +36 20 222 2626 | E-mail: info@muszeroazis.hu | Web: www.muszeroazis.hu</Text>
          </View>
          <View>
            <Text
              style={{ textAlign: "right" }}
              render={({ pageNumber, totalPages }) => `page ${pageNumber} / ${totalPages}`}></Text>
          </View>
        </View>
      </View>
    </Page>
  );

  const resultPages = (
    <Page size="A4" style={[styles.page, { paddingBottom: 70 }]}>
      <View style={{ marginBottom: 10 }} fixed>
        <View style={[styles.row, certstyle.header, { marginBottom: 5 }]}>
          <View>
            <Text style={[styles.fs_1, styles.fw_700]}>COMMED KFT</Text>
          </View>
          <View style={{ width: 150 }}>
            <View style={[styles.row]}>
              <Text style={[styles.fs_3]}>Certificate No:</Text>
              <Text style={[styles.fs_3, styles.fw_700]}>{`${cert.cal_number_prefix ?? null}${cert.cal_number}`}</Text>
            </View>
            <View style={[styles.row]}>
              <Text style={[styles.fs_3]}>Issue date:</Text>
              <Text style={[styles.fs_3]}>{formatDate(cert.cal_date)}</Text>
            </View>
          </View>
        </View>

        <View>
          <Text style={{ fontStyle: "italic" }}>MEASURED VALUES:</Text>
        </View>
      </View>
      {[...paramSetRef.current].map((dev_func, index) => (
        <View key={index} style={{ marginBottom: 20 }} wrap={false}>
          <Text style={styles.fw_700}>{dev_func}</Text>
          <View style={[certstyle.section, { marginTop: 0 }]}>
            <View style={certstyle.resultSection}>
              <View style={certstyle.resultRow}>
                <Text style={styles.fw_700}>Range</Text>
              </View>
              <View style={certstyle.resultRow}>
                <Text style={styles.fw_700}>Standard value</Text>
              </View>
              <View style={certstyle.resultRow}>
                <Text style={styles.fw_700}>Measured value</Text>
              </View>
              <View style={certstyle.resultRow}>
                <Text style={styles.fw_700}>Deviation</Text>
              </View>
              <View style={certstyle.resultRow}>
                <Text style={styles.fw_700}>Uncertanity</Text>
              </View>
              <View style={certstyle.resultRow}>
                <Text style={styles.fw_700}>Qualification</Text>
              </View>
            </View>

            {results
              .sort((a: any, b: any) => {
                const aFactor = Number(siPrefix.data.find((i) => i.id === a.si_prefix_id)?.factor || 1);
                const bFactor = Number(siPrefix.data.find((i) => i.id === b.si_prefix_id)?.factor || 1);

                const aRange = a.range * aFactor;
                const bRange = b.range * bFactor;

                if (aRange !== bRange) {
                  return aRange - bRange;
                }

                const aRef = a.reference_value * aFactor;
                const bRef = b.reference_value * bFactor;

                return aRef - bRef;
              })
              .map(
                (item, index_fu) =>
                  item.device_function === dev_func &&
                  item.visible === 1 && (
                    <View style={certstyle.resultSection} key={index_fu}>
                      <View style={certstyle.resultRow}>
                        <Text>{`${Number(item.range)} ${item.si_prefix_symbol}${item.unit_symbol}`}</Text>
                      </View>
                      <View style={certstyle.resultRow}>
                        <Text>{`${Number(item.reference_value)} ${item.si_prefix_symbol}${item.unit_symbol}`}</Text>
                      </View>
                      <View style={certstyle.resultRow}>
                        <Text>{`${Number(item.measured_value)} ${item.si_prefix_symbol}${item.unit_symbol}`}</Text>
                      </View>
                      <View style={certstyle.resultRow}>
                        <Text>{`${Number(item.deviation)} ${item.si_prefix_symbol}${item.unit_symbol}`}</Text>
                      </View>
                      <View style={certstyle.resultRow}>
                        <Text>{item.uncertainity}</Text>
                      </View>
                      <View style={certstyle.resultRow}>
                        <Text>{job.qualify === 0 ? "---" : item.passed ? "OK" : "NOT OK"}</Text>
                      </View>
                    </View>
                  )
              )}
          </View>
        </View>
      ))}
      <View>
        <Text style={{ textAlign: "center" }}>--- End of document ---</Text>
      </View>
      <View style={certstyle.footer} fixed>
        <Text style={[styles.fs_4, { marginBottom: -5, marginTop: 5, fontSize: 8 }]}>
          The certificate is only valid and may be copied in its full form and scope; an abstract copy may only be made
          with the consent of the issuing organization.
        </Text>
        <View style={[styles.row, certstyle.section]}>
          <View style={[styles.col, { width: "100%" }]}>
            <Text>Commed Kft</Text>
          </View>
          <View style={[styles.col, { width: "10%" }]}>
            <Text
              style={{ textAlign: "right" }}
              render={({ pageNumber, totalPages }) => `page ${pageNumber} / ${totalPages}`}></Text>
          </View>
        </View>
      </View>
    </Page>
  );

  return (
    <Viewer>
      {!loading ? (
        <Document>
          {pageOne}
          {resultPages}
        </Document>
      ) : (
        ""
      )}
    </Viewer>
  );
}
